import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface Product {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  sku?: string;
  category?: 'product' | 'service' | 'subscription' | 'consulting';
  unit_type?: string;
  default_cost?: number;
  default_price?: number;
  default_supplier_id?: string;
  is_active?: boolean;
  metadata?: Record<string, any>;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateProductInput {
  organization_id: string;
  name: string;
  description?: string;
  sku?: string;
  category?: string;
  unit_type?: string;
  default_cost?: number;
  default_price?: number;
  default_supplier_id?: string;
  is_active?: boolean;
  metadata?: Record<string, any>;
}

export interface UpdateProductInput {
  name?: string;
  description?: string;
  sku?: string;
  category?: string;
  unit_type?: string;
  default_cost?: number;
  default_price?: number;
  default_supplier_id?: string;
  is_active?: boolean;
  metadata?: Record<string, any>;
}

class ProductsService {
  /**
   * Get all products for an organization
   */
  async list(organizationId: string): Promise<{ data: Product[] }> {
    validateRequiredParams({ organizationId }, ['organizationId'], { service: 'productsService.list' });
    const url = buildApiUrl('/api/crm/products', { organization_id: organizationId });
    return fetchJSON(url, {
      description: 'List products',
      context: { organizationId }
    });
  }

  /**
   * Get a single product by ID
   */
  async get(productId: string): Promise<{ data: Product }> {
    validateRequiredParams({ productId }, ['productId'], { service: 'productsService.get' });
    const url = buildApiUrl(`/api/crm/products/${encodeURIComponent(productId)}`);
    return fetchJSON(url, {
      description: 'Get product',
      context: { productId }
    });
  }

  /**
   * Create a new product
   */
  async create(product: CreateProductInput): Promise<{ data: Product }> {
    validateRequiredParams(product, ['organization_id', 'name'], { service: 'productsService.create' });
    const url = buildApiUrl('/api/crm/products');
    return fetchJSON(url, {
      method: 'POST',
      body: product,
      description: 'Create product',
      context: { organizationId: product.organization_id }
    });
  }

  /**
   * Update an existing product
   */
  async update(productId: string, updates: UpdateProductInput): Promise<{ data: Product }> {
    validateRequiredParams({ productId }, ['productId'], { service: 'productsService.update' });
    const url = buildApiUrl(`/api/crm/products/${encodeURIComponent(productId)}`);
    return fetchJSON(url, {
      method: 'PATCH',
      body: updates,
      description: 'Update product',
      context: { productId }
    });
  }

  /**
   * Delete a product
   */
  async delete(productId: string): Promise<void> {
    validateRequiredParams({ productId }, ['productId'], { service: 'productsService.delete' });
    const url = buildApiUrl(`/api/crm/products/${encodeURIComponent(productId)}`);
    await fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete product',
      context: { productId }
    });
  }

  /**
   * Search products by name or SKU
   */
  async search(organizationId: string, query: string): Promise<{ data: Product[] }> {
    validateRequiredParams({ organizationId, query }, ['organizationId', 'query'], { service: 'productsService.search' });
    const url = buildApiUrl('/api/crm/products/search', { organization_id: organizationId, q: query });
    return fetchJSON(url, {
      description: 'Search products',
      context: { organizationId, query }
    });
  }
}

const productsService = new ProductsService();
export default productsService;
